import { useMemo } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";
import { SelectOption } from "../../../../types/select";
import { useBookingSelector } from "./useBookingSelector";

export const TOR_RULES = {
  baseRentOnly: "Base Rent Only",
  turnoverRentOnly: "Turnover Rent Only",
  baseRentAndTurnoverRent: "Base Rent + Turnover Rent",
  baseRentOrTurnoverRent: "Base Rent or Turnover (Higher of)",
} as const;

export type TorRuleType = keyof typeof TOR_RULES;

export const BASE_RENT_ONLY: TorRuleType = "baseRentOnly";
export const TURNOVER_RENT_ONLY: TorRuleType = "turnoverRentOnly";
export const BASE_RENT_AND_TURNOVER_RENT: TorRuleType = "baseRentAndTurnoverRent";
export const BASE_RENT_OR_TURNOVER_RENT: TorRuleType = "baseRentOrTurnoverRent";

export const torRulesOptions: SelectOption<TorRuleType>[] = (Object.entries(TOR_RULES) as [TorRuleType, string][]).map(
  ([value, label]) => ({ value, label }),
);

const turnoverAllowedTorValues = new Set<TorRuleType>(
  (Object.keys(TOR_RULES) as TorRuleType[]).filter((value) => value !== BASE_RENT_ONLY),
);

export const useTorRules = () => {
  const { torType: bookingDetailsTorType } = useBookingSelector();
  const bookingFormTorType = useSelector<RootState, string | undefined>(
    (state) => state?.newBooking?.bookingForm?.torType,
  );

  const torType = useMemo(() => {
    return bookingFormTorType ?? bookingDetailsTorType ?? undefined;
  }, [bookingFormTorType, bookingDetailsTorType]);

  const hasValidTorType = torType != null && turnoverAllowedTorValues.has(torType as TorRuleType);

  const isBaseRentOnly = torType === BASE_RENT_ONLY;
  const isTurnoverRentOnly = torType === TURNOVER_RENT_ONLY;

  return {
    torType,
    torRulesOptions,
    hasValidTorType,
    isBaseRentOnly,
    isTurnoverRentOnly,
  };
};
